/**
 * Billing page for subscription management.
 *
 * Displays current subscription status and provides options to:
 * - Subscribe to a plan
 * - Manage subscription (update payment method, cancel)
 */

import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
} from '@mui/material';
import {
  CheckCircle2 as CheckCircleIcon,
  X as CancelIcon,
  CreditCard as CreditCardIcon,
  Settings as SettingsIcon,
} from 'lucide-react';
import billingApi from '../../utils/billingApi';

/**
 * Status badge component with color coding
 */
const StatusBadge = ({ status }) => {
  const statusConfig = {
    active: { label: 'Active', color: 'success', icon: <CheckCircleIcon size={16} /> },
    trialing: { label: 'Trial', color: 'info', icon: <CheckCircleIcon size={16} /> },
    past_due: { label: 'Past Due', color: 'warning', icon: <CancelIcon size={16} /> },
    canceled: { label: 'Canceled', color: 'error', icon: <CancelIcon size={16} /> },
    incomplete: { label: 'Incomplete', color: 'warning', icon: <CancelIcon size={16} /> },
    incomplete_expired: { label: 'Incomplete Expired', color: 'error', icon: <CancelIcon size={16} /> },
    unpaid: { label: 'Unpaid', color: 'error', icon: <CancelIcon size={16} /> },
  };

  const config = statusConfig[status] || statusConfig.incomplete;

  return (
    <Chip
      label={config.label}
      color={config.color}
      icon={config.icon}
      sx={{ fontWeight: 'bold' }}
    />
  );
};

/**
 * Subscription details card component
 */
const SubscriptionDetailsCard = ({ subscription }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Subscription Details
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              Status
            </Typography>
            <Box sx={{ mt: 1 }}>
              <StatusBadge status={subscription.status} />
            </Box>
          </Grid>

          {subscription.plan_details && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Plan
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {subscription.plan_details.name}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {subscription.plan_details.formatted_price}/{subscription.plan_details.interval}
              </Typography>
            </Grid>
          )}

          {subscription.current_period_end && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                {subscription.cancel_at_period_end ? 'Ends On' : 'Renews On'}
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                {formatDate(subscription.current_period_end)}
              </Typography>
              {subscription.days_until_renewal !== null && (
                <Typography variant="caption" color="text.secondary">
                  {subscription.days_until_renewal} days remaining
                </Typography>
              )}
            </Grid>
          )}

          {subscription.cancel_at_period_end && (
            <Grid item xs={12}>
              <Alert severity="warning">
                Your subscription will be canceled at the end of the current billing period.
              </Alert>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

/**
 * Main Billing Page Component
 */
const BillingPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Load subscription status on component mount
  useEffect(() => {
    loadSubscriptionStatus();
  }, []);

  /**
   * Fetch subscription status from API
   */
  const loadSubscriptionStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await billingApi.getSubscriptionStatus();
      setHasSubscription(data.has_subscription);
      if (data.has_subscription) {
        setSubscription(data);
      }
    } catch (err) {
      console.error('Error loading subscription:', err);
      setError('Failed to load subscription status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle subscribe button click - redirects to Stripe Checkout
   */
  const handleSubscribe = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await billingApi.createCheckoutSession();
      // Redirect to Stripe Checkout
      window.location.href = data.url;
    } catch (err) {
      console.error('Error creating checkout session:', err);
      setError('Failed to start checkout. Please try again.');
      setActionLoading(false);
    }
  };

  /**
   * Handle manage subscription button click - redirects to Stripe Customer Portal
   */
  const handleManageSubscription = async () => {
    try {
      setActionLoading(true);
      setError(null);
      const data = await billingApi.createPortalSession();
      // Redirect to Stripe Customer Portal
      window.location.href = data.url;
    } catch (err) {
      console.error('Error creating portal session:', err);
      setError('Failed to open billing portal. Please try again.');
      setActionLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="body1" sx={{ mt: 2 }}>
          Loading subscription information...
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" gutterBottom>
          Billing & Subscription
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Manage your AppealBot subscription and billing information
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {!hasSubscription ? (
          // No subscription - show subscribe option
          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Alert severity="info" sx={{ mb: 3 }}>
              You don't have an active subscription. Subscribe to access premium features.
            </Alert>

            <Card elevation={0} sx={{ border: 2, borderColor: 'primary.main', p: 3, mb: 3 }}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  AppealBot Monthly
                </Typography>
                <Typography variant="h3" color="primary" gutterBottom>
                  $29.99
                  <Typography variant="caption" color="text.secondary">
                    /month
                  </Typography>
                </Typography>
                <Typography variant="body2" color="text.secondary" paragraph>
                  Full access to all features including:
                </Typography>
                <Box component="ul" sx={{ textAlign: 'left', mb: 2 }}>
                  <li>Unlimited appeal generation</li>
                  <li>Claim analysis and recommendations</li>
                  <li>Document management</li>
                  <li>Priority support</li>
                </Box>
              </CardContent>
            </Card>

            <Button
              variant="contained"
              size="large"
              startIcon={<CreditCardIcon size={16} />}
              onClick={handleSubscribe}
              disabled={actionLoading}
            >
              {actionLoading ? 'Redirecting...' : 'Subscribe Now'}
            </Button>
          </Box>
        ) : (
          // Has subscription - show details and management options
          <Box sx={{ mt: 4 }}>
            <SubscriptionDetailsCard subscription={subscription} />

            <Box sx={{ mt: 3, display: 'flex', gap: 2, justifyContent: 'center' }}>
              <Button
                variant="contained"
                startIcon={<SettingsIcon size={16} />}
                onClick={handleManageSubscription}
                disabled={actionLoading}
              >
                {actionLoading ? 'Redirecting...' : 'Manage Subscription'}
              </Button>
            </Box>

            <Alert severity="info" sx={{ mt: 3 }}>
              Use the "Manage Subscription" button to update your payment method, change plans,
              or cancel your subscription.
            </Alert>
          </Box>
        )}
      </Paper>
    </Container>
  );
};

export default BillingPage;
